import React from 'react';
import styled from 'styled-components';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { FormField, RawFormFillerData } from 'shared-utils/types/formFiller';
import { TextFieldInput } from './fields/TextFieldInput';
import { DateFieldInput } from './fields/DateFieldInput';
import { CheckboxFieldInput } from './fields/CheckboxFieldInput';
import { RadioFieldInput } from './fields/RadioFieldInput';
import { SignatureFieldInput } from './fields/SignatureFieldInput';
import { convertCoordinatesToPercent, getFieldState } from '../../lib/formFiller';

const FieldContainer = styled.div<{
  $xPercent: number;
  $yPercent: number;
  $widthPercent: number;
  $heightPercent: number;
}>`
  position: absolute;
  left: ${({ $xPercent }) => $xPercent}%;
  top: ${({ $yPercent }) => $yPercent}%;
  width: ${({ $widthPercent }) => $widthPercent}%;
  height: ${({ $heightPercent }) => $heightPercent}%;
  pointer-events: auto;
  z-index: 100;
`;

const FieldWrapper = styled.div<{ $hasError?: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 2px;

  ${({ $hasError }) =>
    $hasError &&
    `border: 1px solid var(--error-red);
    
    &:hover {
      border-color: var(--error-red);
    }
    
    &:focus-within {
      border-color: var(--error-red);
      box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2);
    }`}
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  font-size: 12px;
  color: var(--error-red);
  background-color: white;
  padding: 2px 4px;
  border-radius: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  z-index: 30;
  margin-top: 2px;
`;

interface FormFieldOverlayProps {
  field: FormField;
  value: any;
  error?: string;
  register: UseFormRegister<RawFormFillerData>;
  setValue: UseFormSetValue<RawFormFillerData>;
  'data-field-id': string;
  signedFields: string[];
}

/**
 * Wrapper component for individual form fields with coordinate-based positioning
 * TODO(adalman): Update this component to use the new FieldOverlayBase component
 */
export const FormFieldOverlay: React.FC<FormFieldOverlayProps> = ({
  field,
  value,
  error,
  register,
  setValue,
  'data-field-id': dataFieldId,
  signedFields,
}) => {
  const { x, y, width, height } = field.coordinates;

  // Use page dimensions for accurate scaling if available, otherwise fall back to default
  const { xPercent, yPercent, widthPercent, heightPercent } = convertCoordinatesToPercent({ x, y, width, height });

  const renderFieldInput = () => {
    const fieldState = getFieldState(field, value);
    const valueToDisplay = field.isReadOnly ? field.readOnlyValue : value;
    const commonProps = {
      fieldState,
      register,
    };

    switch (field.type) {
      case 'text':
        return <TextFieldInput {...commonProps} value={valueToDisplay} />;
      case 'date':
        return (
          <DateFieldInput
            {...commonProps}
            value={valueToDisplay}
            dateFormat={field.format || 'YYYY-MM-DD'}
            autoFilledDate={field.autoFilledDate ?? false}
          />
        );
      case 'checkbox':
        return <CheckboxFieldInput {...commonProps} isChecked={Boolean(valueToDisplay)} />;
      case 'radio':
        return (
          <RadioFieldInput
            {...commonProps}
            value={valueToDisplay}
            groupName={field.group || field.name}
            optionValue={field.value || ''}
          />
        );
      case 'signature':
        return (
          <SignatureFieldInput
            {...commonProps}
            value={valueToDisplay}
            setValue={setValue}
            coordinates={{ width, height }}
            signedFields={signedFields}
          />
        );
      default:
        console.error(`Unknown field type: ${field.type}`);
        return null;
    }
  };

  return (
    <FieldContainer
      $xPercent={xPercent}
      $yPercent={yPercent}
      $widthPercent={widthPercent}
      $heightPercent={heightPercent}
      data-field-id={dataFieldId}
    >
      <FieldWrapper $hasError={!!error}>{renderFieldInput()}</FieldWrapper>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </FieldContainer>
  );
};
